import { useEffect, useRef, type MouseEvent, type RefObject } from 'react';

/**
 * A native `dialog` opened modally while `open` holds, and closed when it stops holding.
 *
 * <p>Modal because the browser then keeps focus inside it, makes the page behind it inert, closes
 * it on Escape and gives focus back to whatever opened it — none of which a positioned `div` does
 * without being taught. The guards matter: showModal() on a dialog already open throws, and a
 * dialog Escape has closed is already shut when `open` catches up.
 */
export function useNativeDialog(open: boolean): RefObject<HTMLDialogElement> {
  const dialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    if (open && !element.open) element.showModal();
    if (!open && element.open) element.close();
  }, [open]);

  return dialog;
}

/**
 * A click handler that closes the dialog when the click lands on the dialog itself — which, for a
 * modal dialog, is its backdrop: a click inside lands on the content drawn over it.
 */
export function useBackdropClose(onClose: () => void) {
  return (event: MouseEvent<HTMLDialogElement>) => {
    if (event.target === event.currentTarget) onClose();
  };
}
